import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { buildBreadcrumbs } from '@/components/feature/Breadcrumb';
import StructuredData, { getOrganizationSchema, getWebPageSchema, getFAQSchema } from '@/components/feature/StructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const breadcrumbs = buildBreadcrumbs('/about');

  useEffect(() => {
    document.title = '关于我们 - 打屁股视频大全 | 品牌介绍与联系方式';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        '打屁股视频大全品牌介绍、口碑评价与联系方式。精选优质视频内容聚合平台，涵盖亲子教育、影视赏析、搞笑短片、历史文化、动漫游戏与社会热点。',
      );
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://dapiguvideo.com/about');
  }, []);

  return (
    <>
      <ScrollToTop />
      <StructuredData data={getOrganizationSchema()} />
      <StructuredData
        data={getWebPageSchema(
          '关于我们 - 打屁股视频大全',
          '打屁股视频大全品牌介绍、口碑评价与联系方式',
          'https://dapiguvideo.com/about',
        )}
      />
      <StructuredData data={getFAQSchema(aboutData.faqs)} />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="relative w-full h-[220px] md:h-[300px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20background%20with%20soft%20gradient%20curves%2C%20cream%20beige%20and%20warm%20amber%20tones%2C%20minimalist%20modern%20design%2C%20smooth%20flowing%20shapes%2C%20professional%20yet%20inviting%20atmosphere%2C%20clean%20composition&width=1600&height=700&seq=about-header-2026&orientation=landscape"
            alt="关于我们"
            title="关于我们 - 打屁股视频大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl">
              了解打屁股视频大全的品牌故事与发展历程
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbs} />
        </section>

        {/* Stats */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aboutData.stats.map((stat) => (
              <div
                key={stat.label}
                className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70"
              >
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
                品牌介绍
              </h2>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <p className="text-foreground-700 leading-relaxed mb-6">
                  {aboutData.brandIntro}
                </p>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                  我们的使命
                </h3>
                <p className="text-foreground-700 leading-relaxed">{aboutData.mission}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
            核心价值观
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {aboutData.values.map((value, idx) => (
              <div
                key={idx}
                className="p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-check-double-line text-lg"></i>
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* User Reputation */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              来自真实用户的评价与反馈
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutData.reputation.map((item, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < item.rating
                            ? 'ri-star-fill text-accent-500'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.comment}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm">
                      {item.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                      <p className="text-xs text-foreground-400">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
            联系我们
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: aboutData.contact.email },
                { icon: 'ri-phone-line', label: '电话', value: aboutData.contact.phone },
                { icon: 'ri-wechat-line', label: '微信', value: aboutData.contact.wechat },
                { icon: 'ri-weibo-line', label: '微博', value: aboutData.contact.weibo },
                { icon: 'ri-map-pin-line', label: '地址', value: aboutData.contact.address },
                { icon: 'ri-time-line', label: '工作时间', value: aboutData.contact.workHours },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400 mb-0.5">{item.label}</p>
                    <p className="text-sm text-foreground-800 font-medium break-all">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutData.faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between p-4 md:p-5 font-medium text-foreground-800 text-sm md:text-base list-none">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}